/**
 * Standardized Component Primitives
 * Provides consistent, theme-aware building blocks
 * Eliminates duplication and ensures design consistency
 */

import { ReactNode, ElementType, MouseEventHandler } from "react";
import { themeClasses, animationClasses, ThemeProps, cn } from "@/lib/theme";

// Base primitive component interface
interface PrimitiveProps extends ThemeProps {
  children?: ReactNode;
  as?: ElementType;
  onClick?: MouseEventHandler<HTMLElement>;
}

/**
 * Themed Text Components
 */
interface TextProps extends PrimitiveProps {
  variant?: "primary" | "secondary" | "muted" | "accent" | "inverse";
  size?: "xs" | "sm" | "base" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
  weight?: "normal" | "medium" | "semibold" | "bold" | "extrabold";
  align?: "left" | "center" | "right";
}

const textSizes = {
  xs: "text-xs",
  sm: "text-sm",
  base: "text-base",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
  "3xl": "text-3xl",
  "4xl": "text-4xl",
  "5xl": "text-5xl",
};

const textWeights = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
  extrabold: "font-extrabold",
};

const textAligns = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

export function ThemedText({
  children,
  konamiMode = false,
  variant = "primary",
  size = "base",
  weight = "normal",
  align = "left",
  className,
  onClick,
  as: Component = "span",
  ...props
}: TextProps) {
  const classes = cn(
    themeClasses.text[variant](konamiMode),
    textSizes[size],
    textWeights[weight],
    textAligns[align],
    onClick && "cursor-pointer",
    className
  );

  return (
    <Component className={classes} onClick={onClick} {...props}>
      {children}
    </Component>
  );
}

/**
 * Themed Button Components
 */
interface ButtonProps extends PrimitiveProps {
  variant?: "primary" | "secondary" | "ghost";
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
}

const buttonSizes = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2 text-base",
  lg: "px-6 py-3 text-lg",
};

export function ThemedButton({
  children,
  konamiMode = false,
  variant = "primary",
  size = "md",
  disabled = false,
  className,
  onClick,
  as: Component = "button",
  ...props
}: ButtonProps) {
  const classes = cn(
    "rounded-xl font-semibold transition-all duration-200 outline-none focus:ring-2 focus:ring-offset-2",
    themeClasses.button[variant](konamiMode),
    buttonSizes[size],
    disabled && "cursor-not-allowed opacity-50",
    !disabled && "hover:scale-[1.02]",
    className
  );

  return (
    <Component
      className={classes}
      onClick={!disabled ? onClick : undefined}
      disabled={disabled}
      {...props}
    >
      {children}
    </Component>
  );
}

/**
 * Themed Card Components
 */
interface CardProps extends PrimitiveProps {
  variant?: "base" | "interactive";
  padding?: "none" | "sm" | "md" | "lg";
}

const cardPadding = {
  none: "",
  sm: "p-4",
  md: "p-6",
  lg: "p-8",
};

export function ThemedCard({
  children,
  konamiMode = false,
  variant = "base",
  padding = "md",
  className,
  onClick,
  as: Component = "div",
  ...props
}: CardProps) {
  const classes = cn(
    "rounded-xl border transition-all duration-300",
    themeClasses.card[variant](konamiMode),
    cardPadding[padding],
    onClick && "cursor-pointer",
    className
  );

  return (
    <Component className={classes} onClick={onClick} {...props}>
      {children}
    </Component>
  );
}

/**
 * Themed Icon Components
 */
interface IconProps extends PrimitiveProps {
  variant?: "primary" | "secondary";
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  hover?: boolean;
}

const iconSizes = {
  xs: "h-3 w-3",
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6",
  xl: "h-8 w-8",
};

export function ThemedIcon({
  children,
  konamiMode = false,
  variant = "primary",
  size = "md",
  hover = false,
  className,
  onClick,
  as: Component = "div",
  ...props
}: IconProps) {
  const classes = cn(
    iconSizes[size],
    themeClasses.icon[variant](konamiMode),
    hover && themeClasses.icon.hover(konamiMode),
    animationClasses.transition,
    onClick && "cursor-pointer",
    className
  );

  return (
    <Component className={classes} onClick={onClick} {...props}>
      {children}
    </Component>
  );
}

/**
 * Themed Link Components
 */
interface LinkProps extends PrimitiveProps {
  variant?: "primary" | "underline";
  href?: string;
  target?: string;
  rel?: string;
}

export function ThemedLink({
  children,
  konamiMode = false,
  variant = "primary",
  className,
  href,
  target,
  rel,
  onClick,
  as: Component = "a",
  ...props
}: LinkProps) {
  const classes = cn(
    themeClasses.link[variant](konamiMode),
    animationClasses.transition,
    className
  );

  return (
    <Component
      className={classes}
      href={href}
      target={target}
      rel={rel}
      onClick={onClick}
      {...props}
    >
      {children}
    </Component>
  );
}

/**
 * Themed Gradient Text
 */
interface GradientTextProps extends PrimitiveProps {
  size?: "sm" | "base" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
  weight?: "normal" | "medium" | "semibold" | "bold" | "extrabold";
  glow?: boolean;
}

export function ThemedGradientText({
  children,
  konamiMode = false,
  size = "base",
  weight = "bold",
  glow = false,
  className,
  onClick,
  as: Component = "span",
  ...props
}: GradientTextProps) {
  const classes = cn(
    themeClasses.gradient.text(konamiMode),
    textSizes[size],
    textWeights[weight],
    glow && themeClasses.shadow.glow(konamiMode),
    onClick && "cursor-pointer",
    className
  );

  return (
    <Component className={classes} onClick={onClick} {...props}>
      {children}
    </Component>
  );
}

/**
 * Themed Badge/Tag Components
 */
interface BadgeProps extends PrimitiveProps {
  variant?: "primary" | "secondary" | "outline";
  size?: "sm" | "md";
}

const badgeSizes = {
  sm: "px-2 py-1 text-xs",
  md: "px-3 py-1.5 text-sm",
};

export function ThemedBadge({
  children,
  konamiMode = false,
  variant = "primary",
  size = "sm",
  className,
  onClick,
  as: Component = "span",
  ...props
}: BadgeProps) {
  const getVariantClasses = () => {
    switch (variant) {
      case "primary":
        return konamiMode
          ? "bg-[#00FFFF]/20 text-[#00FFFF] border border-[#00FFFF]"
          : "bg-green-400/20 text-green-400 border border-green-400";
      case "secondary":
        return konamiMode
          ? "bg-[#0080FF]/20 text-[#0080FF] border border-[#0080FF]"
          : "bg-green-600/20 text-green-300 border border-green-600";
      case "outline":
        return konamiMode
          ? "border border-[#00FFFF] text-[#00FFFF]"
          : "border border-green-400 text-green-400";
      default:
        return "";
    }
  };

  const classes = cn(
    "rounded-full font-medium",
    badgeSizes[size],
    getVariantClasses(),
    onClick && "cursor-pointer",
    className
  );

  return (
    <Component className={classes} onClick={onClick} {...props}>
      {children}
    </Component>
  );
}
